import { DialogComponent } from '@theme/dialog';
import { trapFocus, removeTrapFocus } from '@theme/focus';

/** @type {RsDrawerComponent|null} */
let activeDrawer = null;

/**
 * Reusable side-panel / drawer component.
 *
 * Extends the theme's DialogComponent to add:
 * - Focus trap on open
 * - Focus restore to opener on close
 * - Global trigger support via [data-rs-drawer-open="PANEL_ID"]
 *
 * @extends DialogComponent
 */
class RsDrawerComponent extends DialogComponent {
  /** @type {HTMLElement|null} */
  #openerElement = null;
  #baseCloseDialog = this.closeDialog.bind(this);

  disconnectedCallback() {
    removeTrapFocus();

    if (activeDrawer === this) {
      activeDrawer = null;
    }

    super.disconnectedCallback();
  }

  /**
   * Opens the drawer and traps focus inside.
   *
   * @param {HTMLElement} [opener] - The element that triggered the open.
   */
  showDialog(opener) {
    if (this.refs.dialog?.open) return;

    if (activeDrawer && activeDrawer !== this && activeDrawer.refs.dialog?.open) {
      void activeDrawer.closeDialog().then(() => this.showDialog(opener));
      return;
    }

    if (opener instanceof HTMLElement) {
      this.#openerElement = opener;
    }

    super.showDialog();
    activeDrawer = this;

    // trapFocus after the dialog is shown (next frame, since super uses rAF)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (this.refs.dialog?.open) {
          trapFocus(this.refs.dialog);
        }
      });
    });
  }

  /**
   * Closes the drawer, removes focus trap, restores focus to opener.
   */
  closeDialog = async () => {
    removeTrapFocus();
    await this.#baseCloseDialog();

    if (activeDrawer === this) {
      activeDrawer = null;
    }

    if (this.#openerElement) {
      this.#openerElement.focus();
      this.#openerElement = null;
    }
  };

  get panelId() {
    return this.getAttribute('data-panel-id');
  }
}

if (!customElements.get('rs-drawer-component')) {
  customElements.define('rs-drawer-component', RsDrawerComponent);
}

/**
 * Opens a drawer by panel ID, triggered by the given element.
 *
 * @param {string} panelId
 * @param {HTMLElement} trigger
 */
function openDrawerById(panelId, trigger) {
  const drawer = /** @type {RsDrawerComponent|null} */ (
    document.querySelector(`rs-drawer-component[data-panel-id="${CSS.escape(panelId)}"]`)
  );

  if (drawer) {
    drawer.showDialog(trigger);
  }
}

/*
 * Global event delegation for drawer triggers.
 * Supports two patterns:
 *   1. data-rs-drawer-open="PANEL_ID"  — on any element
 *   2. href="#rs-drawer:PANEL_ID"       — on <a> links (useful inside rich-text)
 */
document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  // Pattern 1: data attribute trigger
  const attrTrigger = event.target.closest('[data-rs-drawer-open]');
  if (attrTrigger instanceof HTMLElement) {
    event.preventDefault();
    const panelId = attrTrigger.getAttribute('data-rs-drawer-open');
    if (panelId) openDrawerById(panelId, attrTrigger);
    return;
  }

  // Pattern 2: href="#rs-drawer:PANEL_ID" link trigger
  const link = event.target.closest('a[href^="#rs-drawer:"]');
  if (link instanceof HTMLAnchorElement) {
    event.preventDefault();
    const panelId = link.hash.replace('#rs-drawer:', '');
    if (panelId) openDrawerById(panelId, link);
  }
});
